/**
 * deferred-posthog — the browser's PostHog client, loaded after the page (FRIDGE-1648).
 *
 * Why. On a cold phone load posthog-js (88 KB compressed) was the largest of the 16 scripts
 * the homepage started at the same moment as its render-blocking stylesheets, and a slow
 * connection shares its bandwidth between them, so analytics delayed the first paint
 * (measured 10/17/2026 in a local replay of production pages: the homepage's first paint
 * 0.21 to 0.25 s sooner without it, and an article's largest paint 0.34 s sooner once the
 * library also waits for the page's images).
 *
 * What. The browser build resolves `posthog-js` to this client (see
 * lib/analytics/posthog-browser-aliases.js). It downloads PostHog's property helpers right after
 * first paint and the real library once the page has loaded, so analytics takes bandwidth from
 * neither the first paint nor the page's images, and it behaves like posthog-js for everything
 * the site does with it:
 * - before the provider calls `init`, a capture is dropped, as posthog-js drops it (FRIDGE-807;
 *   lib/posthog-loaded.ts is how mount-time captures wait);
 * - from `init` on, every capture is kept, so the client reports `__loaded` and announces
 *   lib/posthog-loaded.ts's signal at once (posthog-browser.ts): captures that wait on it are
 *   queued like any other, and `get_property` answers from the visitor's stored PostHog
 *   record, as posthog-js does once init has read it (Sentry's anonymous-user enrichment);
 * - `init` hands posthog-js the provider's token and settings unchanged once the library
 *   arrives; the provider's `loaded` callback runs after the calls made in between have been
 *   replayed, in order, each with its original time and page;
 * - if the visitor leaves before the library has loaded, those events go out once with
 *   navigator.sendBeacon in posthog-js's own wire format, built with PostHog's own property
 *   and bot helpers (posthog-beacon-helpers.ts), the visitor's stored identity and session,
 *   and the provider's before_send. Nothing is sent this way once the library is loaded:
 *   posthog-js then handles its own unload.
 *
 * Framework-free and dependency-injected so the whole contract runs in jest
 * (tests/deferred-posthog-fridge-1648.test.ts).
 */

export interface RealPostHogLike {
  __loaded?: boolean
  init(token: string, config?: Record<string, unknown>, name?: string): unknown
  capture(event: string, properties?: Record<string, unknown> | null, options?: Record<string, unknown>): unknown
  get_property(name: string): unknown
  [method: string]: unknown
}

/** PostHog's own helpers for an event built without the library (posthog-beacon-helpers.ts). */
export interface BeaconHelpers {
  /** posthog-js's event properties: device, browser, page, campaign, search and referrer. */
  eventProperties(): Record<string, unknown>
  /** posthog-js's bot check: the library drops every event from a likely bot. */
  isBot(customBlockedUserAgents?: string[]): boolean
  /** posthog-js's uuidv7, for event, distinct, session and window ids. */
  uuid(): string
}

interface StorageLike {
  getItem(key: string): string | null
}

interface PageContext {
  $current_url?: string
  $host?: string
  $pathname?: string
  title?: string
}

export interface DeferredPostHogDeps {
  /** Downloads the real posthog-js (a dynamic import). */
  loadLibrary(): Promise<RealPostHogLike>
  /** Downloads PostHog's property and bot helpers for the exit beacon. */
  loadBeaconHelpers(): Promise<BeaconHelpers>
  /** Runs `run` once, after the page's first contentful paint. */
  afterFirstPaint(run: () => void): void
  /** Runs `run` once the page has loaded (never before first paint); without it, after first paint. */
  afterPageLoad?(run: () => void): void
  /** Start both downloads after first paint without waiting for init (production host). */
  prewarm?: boolean
  /** Where pagehide and visibilitychange are heard (window). */
  events?: EventTarget
  isHidden?(): boolean
  sendBeacon?(url: string, body: Blob): boolean
  now?(): number
  pageContext?(): PageContext
  storage?: StorageLike | null
  sessionStorage?: StorageLike | null
  cookie?(): string
  /** posthog-js's version, sent as $lib_version like the library does. */
  libVersion: string
  /** Runs once, when init is accepted: from then on a capture is kept, not dropped. */
  onInitialised?(): void
}

export interface DeferredPostHog {
  /** True from init on: a capture is then kept (replayed, or sent by the exit beacon). */
  readonly __loaded: boolean
  init(token: string, config?: Record<string, unknown>): DeferredPostHog
  capture(event: string, properties?: Record<string, unknown> | null, options?: Record<string, unknown>): unknown
  get_property(name: string): unknown
  startSessionRecording(...args: unknown[]): unknown
}

type Queued =
  | { kind: 'capture'; event: string; properties: Record<string, unknown>; options?: Record<string, unknown>; at: number; page: PageContext }
  | { kind: 'call'; method: string; args: unknown[] }

type CapturedEvent = { uuid: string; event: string; properties: Record<string, unknown>; timestamp: string }

/** posthog-js defaults: session_idle_timeout_seconds 30 min; a session never outlives 24 h. */
const SESSION_IDLE_MS = 30 * 60 * 1000
const SESSION_MAX_MS = 24 * 60 * 60 * 1000
const FIRST_PAINT_SAFETY_MS = 5000
const PAGE_LOAD_SAFETY_MS = 2500

/** The ingestion endpoint posthog-js's RequestRouter picks for `api_host` (us / eu / custom). */
export function analyticsEndpoint(apiHost: string | undefined): string {
  let host = String(apiHost || 'https://us.i.posthog.com').trim().replace(/\/$/, '')
  if (host === 'https://app.posthog.com') host = 'https://us.i.posthog.com'
  if (/https:\/\/(app|us|us-assets)(\.i)?\.posthog\.com/i.test(host)) return 'https://us.i.posthog.com/e/'
  if (/https:\/\/(eu|eu-assets)(\.i)?\.posthog\.com/i.test(host)) return 'https://eu.i.posthog.com/e/'
  return `${host}/e/`
}

/** posthog-js's beacon body: `data=` + URI-encoded base64 of the UTF-8 JSON (compression=base64). */
export function encodeBeaconBody(data: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(data))
  let binary = ''
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return 'data=' + encodeURIComponent(btoa(binary))
}

/**
 * Run `run` once on the task after the first contentful paint: now if the page has painted,
 * otherwise when the paint entry arrives, and in any case after a safety timeout (a browser
 * without paint timing, or a page that never paints, still gets analytics).
 */
export function afterFirstPaint(run: () => void, win: Window = window): void {
  let scheduled = false
  let ran = false
  const go = () => {
    if (scheduled) return
    scheduled = true
    win.setTimeout(() => {
      if (ran) return
      ran = true
      run()
    }, 0)
  }
  try {
    const painted = win.performance?.getEntriesByType?.('paint')?.some((entry) => entry.name === 'first-contentful-paint')
    if (painted) {
      go()
      return
    }
    const Observer = (win as Window & { PerformanceObserver?: typeof PerformanceObserver }).PerformanceObserver
    if (typeof Observer === 'function') {
      const observer = new Observer((list) => {
        if (list.getEntries().some((entry) => entry.name === 'first-contentful-paint')) {
          observer.disconnect()
          go()
        }
      })
      observer.observe({ type: 'paint', buffered: true })
    }
  } catch {
    // paint timing unsupported: the safety timeout below still runs it
  }
  win.setTimeout(go, FIRST_PAINT_SAFETY_MS)
}

/**
 * Run `run` once the page has loaded: after the window's load event and never before the first
 * contentful paint, or PAGE_LOAD_SAFETY_MS after that paint when the load event is slow. The
 * page's images and its own scripts finish first, so a download started here takes no bandwidth
 * from them.
 */
export function afterPageLoad(run: () => void, win: Window = window): void {
  let ran = false
  const go = () => {
    if (ran) return
    ran = true
    run()
  }
  afterFirstPaint(() => {
    if (win.document?.readyState === 'complete') {
      win.setTimeout(go, 0)
      return
    }
    win.addEventListener('load', () => win.setTimeout(go, 0), { once: true })
    win.setTimeout(go, PAGE_LOAD_SAFETY_MS)
  }, win)
}

function definedOnly(values: PageContext): Record<string, unknown> {
  return Object.fromEntries(Object.entries(values).filter(([, v]) => v !== undefined))
}

function parseJson(raw: string | null | undefined): unknown {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function createDeferredPostHog(deps: DeferredPostHogDeps): DeferredPostHog {
  const now = deps.now ?? (() => Date.now())
  const pageContext = deps.pageContext ?? (() => ({}))
  let library: RealPostHogLike | null = null
  let libraryRequested = false
  let helpers: BeaconHelpers | null = null
  let helpersRequested = false
  let scheduled = false
  let initArgs: { token: string; config: Record<string, unknown> } | null = null
  let initialised = false
  let ready = false
  let exitListening = false
  let bootstrap: { distinctID?: string; sessionID?: string } | null = null
  const queue: Queued[] = []

  const replay = (ph: RealPostHogLike) => {
    for (const item of queue.splice(0)) {
      try {
        if (item.kind === 'capture') {
          const page = definedOnly({ $current_url: item.page.$current_url, $host: item.page.$host, $pathname: item.page.$pathname })
          ph.capture(item.event, { ...page, ...item.properties }, { ...(item.options || {}), timestamp: new Date(item.at) })
        } else {
          const fn = ph[item.method]
          if (typeof fn === 'function') fn.apply(ph, item.args)
        }
      } catch {
        // one failing call must not stop the rest of the queue
      }
    }
  }

  const initialise = () => {
    if (!library || !initArgs || initialised) return
    initialised = true
    const { token, config } = initArgs
    const providerLoaded = config.loaded as ((ph: unknown) => void) | undefined
    const settings: Record<string, unknown> = {
      ...config,
      loaded: (ph: RealPostHogLike) => {
        replay(ph)
        ready = true
        providerLoaded?.(ph)
      },
    }
    if (bootstrap && config.bootstrap === undefined) settings.bootstrap = bootstrap
    try {
      library.init(token, settings)
    } catch {
      // posthog-js failed to start: the queue stays for the exit beacon
      initialised = false
    }
  }

  const startHelpers = () => {
    if (!helpersRequested) {
      helpersRequested = true
      deps.loadBeaconHelpers().then(
        (h) => {
          helpers = h
        },
        () => {},
      )
    }
  }

  const startLibrary = () => {
    if (!libraryRequested) {
      libraryRequested = true
      deps.loadLibrary().then(
        (lib) => {
          library = lib
          initialise()
        },
        () => {},
      )
    }
  }

  const schedule = () => {
    if (scheduled) return
    scheduled = true
    if (deps.afterPageLoad) {
      // the helpers first: they are small, and an exit before the library arrives needs them
      deps.afterFirstPaint(startHelpers)
      deps.afterPageLoad(startLibrary)
    } else {
      deps.afterFirstPaint(() => {
        startHelpers()
        startLibrary()
      })
    }
  }

  const readStoredIdentity = (token: string): Record<string, unknown> | null => {
    const key = `ph_${token}_posthog`
    let raw: string | null | undefined
    try {
      raw = deps.storage?.getItem(key)
    } catch {
      raw = null
    }
    if (!raw && deps.cookie) {
      try {
        const match = deps.cookie().match(new RegExp(`(?:^|;\\s*)${key}=([^;]*)`))
        raw = match ? decodeURIComponent(match[1]) : null
      } catch {
        raw = null
      }
    }
    const parsed = parseJson(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
  }

  const identityFor = (token: string, h: BeaconHelpers) => {
    const stored = readStoredIdentity(token)
    const t = now()
    let distinctId = typeof stored?.distinct_id === 'string' && stored.distinct_id ? stored.distinct_id : null
    const generatedDistinct = !distinctId
    if (!distinctId) distinctId = h.uuid()
    const deviceId = typeof stored?.$device_id === 'string' && stored.$device_id ? stored.$device_id : distinctId
    const identified = stored?.$user_state === 'identified'
    const ses = stored?.$sesid
    let sessionId: string | null = null
    if (Array.isArray(ses) && typeof ses[1] === 'string' && t - Number(ses[0]) < SESSION_IDLE_MS && t - Number(ses[2]) < SESSION_MAX_MS) sessionId = ses[1]
    const generatedSession = !sessionId
    if (!sessionId) sessionId = h.uuid()
    let windowId: string | null = null
    try {
      const rawWindow = deps.sessionStorage?.getItem(`ph_${token}_window_id`) ?? null
      const parsedWindow = parseJson(rawWindow)
      windowId = typeof parsedWindow === 'string' ? parsedWindow : rawWindow
    } catch {
      windowId = null
    }
    if (!windowId) windowId = h.uuid()
    if (generatedDistinct || generatedSession) {
      bootstrap = { ...(generatedDistinct ? { distinctID: distinctId } : {}), ...(generatedSession ? { sessionID: sessionId } : {}) }
    }
    return { distinctId, deviceId, sessionId, windowId, identified }
  }

  const applyBeforeSend = (beforeSend: unknown, event: CapturedEvent): CapturedEvent | null => {
    const fns = Array.isArray(beforeSend) ? beforeSend : beforeSend ? [beforeSend] : []
    let current: CapturedEvent | null = event
    for (const fn of fns) {
      if (!current) break
      if (typeof fn !== 'function') continue
      try {
        current = fn(current) as CapturedEvent | null
      } catch {
        current = null
      }
    }
    return current
  }

  const sendQueueByBeacon = () => {
    if (ready || !initArgs || !helpers || !deps.sendBeacon) return
    const captures = queue.filter((q): q is Extract<Queued, { kind: 'capture' }> => q.kind === 'capture')
    if (captures.length === 0) return
    // delivered once: never replayed if the page comes back and the library loads
    for (const c of captures) queue.splice(queue.indexOf(c), 1)
    const { token, config } = initArgs
    const h = helpers
    try {
      // posthog-js: a likely bot's events are dropped unless opt_out_useragent_filter is set,
      // and with it set every event says which it is in $browser_type.
      const optOutOfBotFilter = config.opt_out_useragent_filter === true
      const likelyBot = h.isBot(config.custom_blocked_useragents as string[] | undefined)
      if (likelyBot && !optOutOfBotFilter) return
      const identity = identityFor(token, h)
      const computed = h.eventProperties()
      const batch: CapturedEvent[] = []
      for (const c of captures) {
        const properties: Record<string, unknown> = {
          ...computed,
          ...definedOnly({ $current_url: c.page.$current_url, $host: c.page.$host, $pathname: c.page.$pathname }),
          ...(c.event === '$pageview' && c.page.title !== undefined ? { title: c.page.title } : {}),
          ...c.properties,
          token,
          distinct_id: identity.distinctId,
          $device_id: identity.deviceId,
          $session_id: identity.sessionId,
          $window_id: identity.windowId,
          $is_identified: identity.identified,
          $process_person_profile: identity.identified,
          $lib: 'web',
          $lib_version: deps.libVersion,
          $time: c.at / 1000,
          ...(optOutOfBotFilter ? { $browser_type: likelyBot ? 'bot' : 'browser' } : {}),
        }
        const event = applyBeforeSend(config.before_send, { uuid: h.uuid(), event: c.event, properties, timestamp: new Date(c.at).toISOString() })
        if (event) batch.push(event)
      }
      if (batch.length === 0) return
      const url = `${analyticsEndpoint(config.api_host as string | undefined)}?compression=base64`
      const body = new Blob([encodeBeaconBody({ api_key: token, batch, sent_at: new Date(now()).toISOString() })], {
        type: 'application/x-www-form-urlencoded',
      })
      deps.sendBeacon(url, body)
    } catch {
      // best effort on unload, like posthog-js's own beacon
    }
  }

  const listenForExit = () => {
    if (exitListening || !deps.events) return
    exitListening = true
    deps.events.addEventListener('pagehide', sendQueueByBeacon)
    deps.events.addEventListener('visibilitychange', () => {
      if (!deps.isHidden || deps.isHidden()) sendQueueByBeacon()
    })
  }

  if (deps.prewarm) schedule()

  const client: DeferredPostHog = {
    get __loaded() {
      return initArgs !== null
    },
    init(token: string, config: Record<string, unknown> = {}) {
      if (initArgs) return client
      initArgs = { token, config }
      listenForExit()
      schedule()
      initialise()
      try {
        deps.onInitialised?.()
      } catch {
        // a listener's failure must not undo init
      }
      return client
    },
    capture(event: string, properties?: Record<string, unknown> | null, options?: Record<string, unknown>) {
      if (ready && library) return library.capture(event, properties ?? undefined, options)
      if (!initArgs) return undefined
      queue.push({ kind: 'capture', event, properties: { ...(properties || {}) }, options, at: now(), page: pageContext() })
      return undefined
    },
    get_property(name: string) {
      if (ready && library) return library.get_property(name)
      if (!initArgs) return undefined
      // posthog-js answers from the persistence init read; until it is here, the same record
      return readStoredIdentity(initArgs.token)?.[name]
    },
    startSessionRecording(...args: unknown[]) {
      if (ready && library && typeof library.startSessionRecording === 'function') return (library.startSessionRecording as (...a: unknown[]) => unknown)(...args)
      if (initArgs) queue.push({ kind: 'call', method: 'startSessionRecording', args })
      return undefined
    },
  }
  return client
}
